import { toaster } from '@optiaxiom/react';
import { type Query, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useOpalUser } from '@frontend/hooks';

import {
  type EmailDomainResponse,
  getEmailDomain,
  updateEmailDomain,
  type UpdateEmailDomainRequest,
} from '../services/emailDomainService';

type UseGetEmailDomainReturn = {
  emailDomainData: EmailDomainResponse | undefined;
  isPending: boolean;
  error: Error | null;
};

type UseUpdateEmailDomainReturn = {
  updateEmailDomain: (variables: UpdateEmailDomainRequest) => void;
  isUpdating: boolean;
  error: Error | null;
};

export function useGetEmailDomain(): UseGetEmailDomainReturn {
  const { opalUser } = useOpalUser();
  const instanceId = opalUser?.instance?.instance_id;

  const {
    data: emailDomainData,
    isPending,
    error,
  } = useQuery<EmailDomainResponse, Error>({
    queryKey: instanceId ? getEmailDomain({ instanceId }).key : [],
    queryFn: () => {
      if (!instanceId) throw new Error('instanceId is required');
      return getEmailDomain({ instanceId })();
    },
    enabled: !!instanceId,
    refetchInterval: (query: Query<EmailDomainResponse, Error>) => {
      // Poll every 3 seconds if email domain is not verified
      return query.state?.data?.email_domain_verified === false ? 3000 : false;
    },
  });

  return { emailDomainData, isPending, error };
}

export function useUpdateEmailDomain(): UseUpdateEmailDomainReturn {
  const queryClient = useQueryClient();
  const { opalUser } = useOpalUser();
  const instanceId = opalUser?.instance?.instance_id;

  const { mutate, isPending, error } = useMutation<
    EmailDomainResponse,
    Error,
    UpdateEmailDomainRequest
  >({
    mutationFn: ({ email_domain }: UpdateEmailDomainRequest) => {
      if (!instanceId) throw new Error('instanceId is required');
      return updateEmailDomain({
        instanceId,
        email_domain,
      });
    },
    onSuccess: (data: EmailDomainResponse) => {
      if (instanceId) {
        queryClient.setQueryData(getEmailDomain({ instanceId }).key, data);
      }
      toaster.create('Email domain updated successfully', { type: 'success' });
    },
    onError: (err: Error) => {
      toaster.create('Failed to update email domain', { type: 'danger' });
      console.error('Error updating email domain:', err);
    },
  });

  return { updateEmailDomain: mutate, isUpdating: isPending, error };
}
